import { useMemo } from 'react';
import type { ClassificationLabelResponse } from '@vagusx/clients';
import type { LabelCategory } from '@vagusx/shared';
import { useLabels } from '@/hooks/use-labels';
import { labelColorClass } from './label-colors';

export interface LabelOption {
  value: string;
  label: string;
  color?: string;
}

export interface SelectLabelOptionsOpts {
  /** Narrow to a single label category. Omit to surface every category. */
  category?: LabelCategory;
  /**
   * Include labels with `enabled = false`. Defaults to `false` — admins
   * disable labels in /setup/labels precisely to hide them from active
   * use, and a filter dropdown is the canonical "active use" surface.
   */
  includeDisabled?: boolean;
}

/**
 * Pure converter from `useLabels()` rows to `FilterBar` option shape.
 * Exported for testing and for callers that already have the labels
 * array in scope; most callers should use the {@link useLabelOptions}
 * hook instead, which handles fetch + memoization in one call.
 *
 * Forward-compat note: `labelCategory` is `string` on the API response
 * (not the strict union) so a category we don't yet recognize survives
 * a no-filter call but is excluded under a category filter — both
 * behaviors verified in the colocated test suite.
 */
export function selectLabelOptions(
  labels: ClassificationLabelResponse[] | undefined,
  opts?: SelectLabelOptionsOpts
): LabelOption[] {
  const all = labels ?? [];
  const filtered = all.filter((l) => {
    if (opts?.category && l.labelCategory !== opts.category) return false;
    if (!opts?.includeDisabled && !l.enabled) return false;
    return true;
  });

  return filtered.map((l) => ({
    value: l.name,
    label: l.displayName,
    color: labelColorClass(l.color),
  }));
}

/**
 * Fetch labels via `useLabels()` and memoize the derived options.
 * Consumers don't need to wrap in their own `useMemo` — the returned
 * array reference is stable across renders that don't change the
 * underlying labels or options bag.
 */
export function useLabelOptions(opts?: SelectLabelOptionsOpts): LabelOption[] {
  const { data: labels } = useLabels();
  return useMemo(
    () => selectLabelOptions(labels, opts),
    [labels, opts?.category, opts?.includeDisabled]
  );
}
